import {
    CSSProperties,
    forwardRef,
    ReactNode,
    type ComponentPropsWithoutRef,
} from 'react'
import cl from 'clsx'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import {
    CreativeSelect,
    type CreativeSelectProps,
    CreativeBadge,
    CreativeSelectTrigger,
} from '@/features/creative/creative-select'
import {
    CreativePreview,
    type CreativePreviewProps,
} from '@/features/creative/preview'
import { ShareCreative, type MarkDataInput } from '@/features/creative/share'
import { type ICreative } from '@/shared/api'
import {
    workspaceWithLinkMethods,
    linkMethods,
} from '@synapex/shared2/entities'

type PublishedTooltipProps = { trigger: ReactNode }
const PublishedTooltip = ({ trigger }: PublishedTooltipProps) => {
    return (
        <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>

            <TooltipContent>
                <p>Этот креатив уже опубликован. Его нельзя изменить</p>
            </TooltipContent>
        </Tooltip>
    )
}

export type CreativeCellProps = {
    published: boolean
    privateChannel?: boolean
    postDeleted?: boolean
    disabled?: boolean
    allowShare?: boolean
    withShare?: boolean
    className?: string

    previewOverlayStyle?: CreativePreviewProps['overlayStyle']
    previewContentStyle?: CreativePreviewProps['contentStyle']
    selectTriggerStyle?: CSSProperties

    marking?: MarkDataInput
    markableFile?: ICreative['markableFile']
} & CreativeSelectProps &
    Omit<ComponentPropsWithoutRef<'div'>, 'className'>
export const CreativeCell = forwardRef<HTMLDivElement, CreativeCellProps>(
    function CreativeCell(
        {
            published,
            privateChannel = false,
            postDeleted = false,
            disabled = false,
            allowShare = true,
            withShare = false,
            className,
            activeCreative,
            previewOverlayStyle,
            previewContentStyle,
            selectTriggerStyle,
            onTouched,
            groupId,
            triggerStyle,
            marking,
            markableFile,
            ...props
        },
        ref
    ) {
        const [wwld] = workspaceWithLinkMethods.extractWwld(props)

        let main: ReactNode

        if (published || disabled) {
            main = activeCreative ? (
                <CreativeBadge data={activeCreative} />
            ) : (
                <CreativeSelectTrigger disabled />
            )

            if (published) {
                main = <PublishedTooltip trigger={main} />
            }
        } else {
            main = (
                <CreativeSelect
                    {...{
                        ...wwld,
                        onTouched,
                        groupId,
                        triggerStyle,
                        activeCreative,
                    }}
                    triggerStyle={selectTriggerStyle}
                />
            )
        }

        return (
            <div
                {...props}
                ref={ref}
                className={cl('flex gap-[3px]', className)}
            >
                {main}
                {activeCreative && (
                    <>
                        <CreativePreview
                            {...wwld}
                            published={published}
                            privateChannel={privateChannel}
                            postDeleted={postDeleted}
                            contentStyle={previewContentStyle}
                            overlayStyle={previewOverlayStyle}
                        />
                        {withShare && (
                            <ShareCreative
                                {...linkMethods.pickLd(wwld)}
                                withTooltip={!allowShare}
                                marking={marking}
                                markableFile={markableFile}
                                {...(allowShare
                                    ? {}
                                    : {
                                          disabled: () => void {},
                                      })}
                            />
                        )}
                    </>
                )}
            </div>
        )
    }
)
